import { useTranslation } from "react-i18next";
import type { AskedInput, ExposedInput } from "../../../../api/content/stepPlans";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import PlannedInputsList, { type GivenValues } from "../../stepPlans/PlannedInputsList";
import StepKindMark from "../../../workflows/graph/StepKindMark";
import { StartStepRoute } from "../../llmChoice/StartRoutes";
import type { PreparedStartState } from "./usePreparedStart";

interface StartPlanProps {
  /** The project a recorded-result input picks its result in (A8). */
  projectId: string;
  state: PreparedStartState;
  values: GivenValues;
  onValuesChange: (values: GivenValues) => void;
  disabled: boolean;
}

/**
 * What the start asks of the picked Workflow, as the server prepares it (ADR-0032 D7; built
 * provisionally): the values it takes, then its steps folded into one line (TR-A2-05; F99,
 * F100). A Thread binding, such as the entry, is asked by its own field above; the steps'
 * prompts and wiring belong to the Thread, where each step runs. The server decides all of
 * it; this view renders it.
 */
export default function StartPlan({ projectId, state, values, onValuesChange, disabled }: StartPlanProps) {
  const { t } = useTranslation();
  if (state.status === "idle") return null;
  if (state.status === "failed") return <ErrorNotice error={state.error} compact={true} />;
  const prepared = state.status === "loaded" ? state.value : state.previous;
  if (prepared === null) return <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.run.preparing")}</p>;
  const names = new Map(prepared.steps.map((plan) => [plan.stepKey, plan.name]));
  // The Workflow's inputs a value fills (a binding has its own field above), then the step inputs the start is asked for.
  const exposed = prepared.inputs.filter((input) => input.binding === null);
  const byName = new Set<ExposedInput | AskedInput>(exposed);
  const asked = [...exposed, ...prepared.asked];
  return (
    <>
      {asked.length === 0 ? null : (
        <PlannedInputsList
          inputs={asked}
          stepName={(key) => names.get(key) ?? key}
          values={disabled ? null : values}
          onChange={onValuesChange}
          pickResultsIn={projectId}
          keyOf={(input) => (byName.has(input) ? input.name : input.destination)}
        />
      )}
      <details>
        <summary className={`${FORM_HELP_TEXT_CLASS} cursor-pointer`}>{t("threads.plan.stepCount", { count: prepared.steps.length })}</summary>
        <ol className="mt-1.5 space-y-1">
          {prepared.steps.map((plan, index) => (
            <li key={plan.stepKey} className="space-y-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span>{index + 1}. {plan.name}</span>
                <StepKindMark kind={plan.stepKind} needsPerson={plan.needsPerson} />
              </div>
              <StartStepRoute plan={plan} />
            </li>
          ))}
        </ol>
      </details>
    </>
  );
}
